import type { FreeeCostTransaction, FreeeRecurringCost, Freshness } from './api'
import { compareYen, sumYen } from './format'

// Display logic for the recurring-costs table on the "Costs (freee)" tab: the
// "past year" column, the per-vendor drill-down and the column sort. All of it
// is computed in the browser over rows the read API already returns (the cost
// lines and the recurring streams) and summed decimal-exactly via `sumYen`.

/**
 * The date "past year" is measured back from: today, clamped to the last
 * successful freee sync. Same clock the backend's recurring-status inference
 * uses (freee_recurring_costs `as_of`): if the sync stalls, measuring against
 * the wall clock would quietly shrink every stream's past-year spend, a number
 * decaying because a pipe broke rather than because spend changed.
 * `today` is an ISO date (YYYY-MM-DD, UTC), injected so tests stay fixed.
 */
export function freeeAsOf(freshness: Freshness[], today: string): string {
  const lastSync = freshness.find((f) => f.job === 'freee')?.lastSucceededAt ?? null
  if (lastSync === null) return today
  const syncDate = lastSync.slice(0, 10)
  return syncDate < today ? syncDate : today
}

/**
 * Exclusive lower bound of the past-year window: the same calendar day one year
 * before `asOf`. A line is in the past year when its issue date is AFTER this.
 * Plain string arithmetic on ISO dates (no Date, so no UTC/local day shift); a
 * Feb 29 bound that doesn't exist the year before still compares correctly.
 */
export function pastYearAfter(asOf: string): string {
  const year = Number(asOf.slice(0, 4))
  return `${String(year - 1).padStart(4, '0')}${asOf.slice(4)}`
}

/** Cost lines grouped by vendor stream, keeping the API's newest-first order. */
export function linesByVendor(transactions: FreeeCostTransaction[]): Map<string, FreeeCostTransaction[]> {
  const byVendor = new Map<string, FreeeCostTransaction[]>()
  for (const t of transactions) {
    const list = byVendor.get(t.vendorKey) ?? []
    list.push(t)
    byVendor.set(t.vendorKey, list)
  }
  return byVendor
}

/** Expense lines only: the recurring view sums expenses, so its totals do too. */
export function expenseLines(lines: FreeeCostTransaction[]): FreeeCostTransaction[] {
  return lines.filter((t) => t.entrySide === 'expense')
}

/**
 * Whether a line falls in the 12 months to `asOf`: after the same day a year
 * earlier, and not after `asOf` itself (a future-dated freee deal is not
 * past-year spend, whatever its amount).
 */
export function inPastYear(issueDate: string, asOf: string): boolean {
  return issueDate > pastYearAfter(asOf) && issueDate <= asOf
}

/** Expense spend on a stream's lines in the 12 months to `asOf`. */
export function pastYearSpend(lines: FreeeCostTransaction[], asOf: string): string {
  return sumYen(expenseLines(lines).filter((t) => inPastYear(t.issueDate, asOf)).map((t) => t.amount))
}

export type RecurringSortKey = 'cadence' | 'vendor' | 'months' | 'latest' | 'pastYear' | 'total'
export type RecurringSort = { key: RecurringSortKey; dir: 'asc' | 'desc' }

// The table's original order: recurring cadences first, heaviest spend within.
export const DEFAULT_SORT: RecurringSort = { key: 'cadence', dir: 'asc' }

const CADENCE_ORDER: Record<string, number> = { monthly: 0, annual: 1, irregular: 2, one_off: 3 }

/** Next sort after clicking a column heading: same column flips, a new one starts at its natural direction. */
export function nextSort(current: RecurringSort, key: RecurringSortKey): RecurringSort {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
  // Names and cadence read A→Z / recurring-first; numbers read biggest-first.
  return { key, dir: key === 'vendor' || key === 'cadence' ? 'asc' : 'desc' }
}

/**
 * Sort a group's streams. Ties fall back to total spend (heaviest first) and
 * then the vendor key, so the order is stable across reloads.
 */
export function sortStreams(
  rows: FreeeRecurringCost[],
  sort: RecurringSort,
  pastYear: (vendorKey: string) => string,
): FreeeRecurringCost[] {
  const sign = sort.dir === 'asc' ? 1 : -1
  const primary = (a: FreeeRecurringCost, b: FreeeRecurringCost): number => {
    switch (sort.key) {
      case 'cadence':
        return (CADENCE_ORDER[a.cadence] ?? 9) - (CADENCE_ORDER[b.cadence] ?? 9)
      case 'vendor':
        return a.displayName.localeCompare(b.displayName)
      case 'months':
        return a.distinctMonths - b.distinctMonths
      case 'latest':
        return compareYen(a.latestAmount, b.latestAmount)
      case 'pastYear':
        return compareYen(pastYear(a.vendorKey), pastYear(b.vendorKey))
      case 'total':
        return compareYen(a.totalAmount, b.totalAmount)
    }
  }
  return rows
    .slice()
    .sort(
      (a, b) =>
        sign * primary(a, b) ||
        compareYen(b.totalAmount, a.totalAmount) ||
        a.vendorKey.localeCompare(b.vendorKey),
    )
}
